import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, ArrowUp, ArrowDown, Plus, Clapperboard, Video, Image as ImageIcon } from 'lucide-react';
import { platformLabel } from '@/lib/clipPlatformFormats';

const KIND_ICON = { clip: Clapperboard, video: Video, image: ImageIcon };

/**
 * Stories list + active story's page sequencing — pages only ever
 * reference EXISTING clips/videos/an image URL, nothing is duplicated or
 * re-uploaded (section 8: "Do not force users to recreate or re-upload
 * shared assets when moving between these workflows").
 */
export function StoryManagerPanel({
  stories, activeStoryId, onSelectStory, onDeleteStory,
  activeStory, activeStoryVariant, onSelectStoryVariant,
  clips, siblingSources,
  onAddPageFromClip, onAddPageFromVideo, onAddPageFromImageUrl, onRemovePage, onReorderPage,
  activePageIdx, onSelectPage,
}) {
  const [imageUrl, setImageUrl] = useState('');

  return (
    <div className="space-y-4" data-testid="story-manager-panel">
      <div>
        <p className="text-[11px] uppercase tracking-wide text-[#555E6E] mb-1.5">Your Stories ({stories.length})</p>
        {stories.length === 0 ? (
          <p className="text-xs text-[#8A93A6] text-center py-4" data-testid="story-list-empty">No Stories yet — create one in Generate.</p>
        ) : (
          <div className="space-y-1.5">
            {stories.map((s) => (
              <div key={s.id} onClick={() => onSelectStory(s.id)} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${s.id === activeStoryId ? 'border-[#3A7BFF] bg-[#3A7BFF]/10' : 'border-white/10 hover:bg-white/5'}`} data-testid={`story-list-item-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-white truncate">{s.name}</p>
                  <p className="text-[10px] text-[#8A93A6]">{s.variants.map((v) => platformLabel(v.platform)).join(', ')}</p>
                </div>
                <button onClick={(e) => { e.stopPropagation(); onDeleteStory(s.id); }} className="text-red-400 hover:text-red-300 shrink-0" data-testid={`story-list-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {activeStory && (
        <>
          {activeStory.variants.length > 1 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {activeStory.variants.map((v) => (
                <button key={v.id} onClick={() => onSelectStoryVariant(v.id)} className={`text-[10px] px-2 py-1 rounded-full border ${v.id === activeStoryVariant?.id ? 'border-[#3A7BFF] bg-[#3A7BFF]/15 text-white' : 'border-white/10 text-[#8A93A6]'}`} data-testid={`story-variant-pill-${v.id}`}>
                  {platformLabel(v.platform)}
                </button>
              ))}
            </div>
          )}

          <div>
            <p className="text-[11px] uppercase tracking-wide text-[#555E6E] mb-1.5">Pages ({activeStoryVariant?.pages.length || 0})</p>
            <div className="space-y-1 mb-2">
              {(activeStoryVariant?.pages || []).map((p, idx) => {
                const Icon = KIND_ICON[p.kind] || Clapperboard;
                return (
                  <div key={p.id} className={`flex items-center gap-1.5 p-1.5 rounded-md border ${idx === activePageIdx ? 'border-[#3A7BFF] bg-[#3A7BFF]/10' : 'border-white/10 hover:bg-white/5'}`} data-testid={`story-page-row-${p.id}`}>
                    <Icon className="h-3.5 w-3.5 text-[#8A93A6] shrink-0" />
                    <button onClick={() => onSelectPage(idx)} className="flex-1 min-w-0 text-left text-xs text-white truncate">{idx + 1}. {p.label}</button>
                    <button onClick={() => onReorderPage(p.id, -1)} disabled={idx === 0} className="disabled:opacity-20 text-[#8A93A6] hover:text-white" data-testid={`story-page-up-${p.id}`}><ArrowUp className="h-3 w-3" /></button>
                    <button onClick={() => onReorderPage(p.id, 1)} disabled={idx === (activeStoryVariant?.pages.length || 0) - 1} className="disabled:opacity-20 text-[#8A93A6] hover:text-white" data-testid={`story-page-down-${p.id}`}><ArrowDown className="h-3 w-3" /></button>
                    <button onClick={() => onRemovePage(p.id)} className="text-red-400 hover:text-red-300" data-testid={`story-page-remove-${p.id}`}><Trash2 className="h-3 w-3" /></button>
                  </div>
                );
              })}
              {(activeStoryVariant?.pages.length || 0) === 0 && <p className="text-xs text-[#8A93A6] text-center py-3" data-testid="story-pages-empty">No pages yet — add one below.</p>}
            </div>

            <div className="space-y-1.5 p-2 rounded-lg border border-dashed border-white/15">
              <Select onValueChange={onAddPageFromClip}>
                <SelectTrigger className="h-7 text-[11px] bg-white/5 border-white/10" data-testid="story-add-page-clip-select"><SelectValue placeholder="+ Add existing clip" /></SelectTrigger>
                <SelectContent>{clips.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select onValueChange={onAddPageFromVideo}>
                <SelectTrigger className="h-7 text-[11px] bg-white/5 border-white/10" data-testid="story-add-page-video-select"><SelectValue placeholder="+ Add existing video" /></SelectTrigger>
                <SelectContent>{siblingSources.map((s) => <SelectItem key={s.id} value={s.id}>{s.title || s.id}</SelectItem>)}</SelectContent>
              </Select>
              <div className="flex items-center gap-1.5">
                <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="Image URL" className="h-7 text-[11px] bg-white/5 border-white/10" data-testid="story-add-page-image-input" />
                <Button size="sm" variant="outline" className="h-7 border-white/10 shrink-0" onClick={() => { onAddPageFromImageUrl(imageUrl); setImageUrl(''); }} data-testid="story-add-page-image-button"><Plus className="h-3 w-3" /></Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
